import { Layout } from "components";
import { styled } from "@mui/system";
import { Box } from "@mui/material";
import { Typography } from "theme";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SignInInfoFormScheme, signInSchema } from "./validation.ts";
import { useCallback } from "react";
import ControlledTextField from "../../components/ControlledInputs/ControlledTextField.tsx";
import { InvertButton } from "../../theme/components/Button/Button.tsx";
import { useMutation } from "@tanstack/react-query";
import { Urls } from "../../api/urls.ts";
import { User } from "../../utils/context/Auth/user.ts";
import { Routes } from "router";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/context/Auth/AuthContext.tsx";
import useFetch from "../../utils/hooks/useFetch.ts";
import { toast } from "react-toastify";

const FormWrapper = styled(Box)(() => ({
  display: "flex",
  justifyContent: 'center',
  alignItems: 'center',
}));

const StyledForm = styled(Box)(() => ({
  maxWidth: '454px',
  width: '100%',
}));

const SignIn = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const request = useFetch();

  const { mutate: signIn, isPending } = useMutation({
    mutationFn: ({ email, password }: SignInInfoFormScheme) => {
      return request<User>(`${Urls.Login}?email=${email}&password=${password}`, {
        method: "POST",
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
    onSuccess: (data) => {
      if (!data) {
        return;
      }

      setUser(data);

      navigate(Routes.Root, {
        replace: true,
      });
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<SignInInfoFormScheme>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: "",
      password: ""
    },
  });

  const onSubmit:SubmitHandler<SignInInfoFormScheme> = useCallback((data) => {
    if (!isDirty) return;

    signIn(data);
  }, [isDirty, signIn])

  return (
    <Layout>
      <FormWrapper>
        <StyledForm>
          <Typography variant='h1' textTransform='uppercase' textAlign='center'>Вхід</Typography>
          <Typography mt={2} variant='body1' textTransform='uppercase' textAlign='center'>Введіть ім'я користувача та пароль, щоб продовжити </Typography>
          <Box
            mt={3.5}
            component="form"
            id="sign-in-form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box>
              <ControlledTextField
                control={control}
                required
                name="email"
                label="Email address"
                type="email"
                autoComplete="off"
              />
              {errors.email?.message && <Typography color='error' variant='body2'>{errors.email.message}</Typography>}
            </Box>
            <Box mt={2}>
              <ControlledTextField
                control={control}
                required
                name="password"
                label="Пароль"
                type="password"
                autoComplete="off"
              />
              {errors.password?.message && <Typography color='error' variant='body2'>{errors.password.message}</Typography>}
            </Box>
            <Box mt={4}>
              <InvertButton
                type="submit"
                form='sign-in-form'
                loading={isPending}
                size="large"
                fullWidth
              >
                Увійти
              </InvertButton>
            </Box>
          </Box>
        </StyledForm>
      </FormWrapper>
    </Layout>
  );
};

export default SignIn;
